import { useEffect, useRef } from 'react';
import * as Dialog from '@radix-ui/react-dialog';

import { Button } from '@/components/Button/Button';

import type { ApiError } from '@/api/client.types';

import { useResetStudentView } from './useResetStudentView';
import styles from './StudentViewResetDialog.module.css';

// The server's own text never reaches the page (docs/engineering/frontend.md):
// each kind of failure gets words of its own.
function describeResetFailure(error: ApiError | null): string {
  switch (error?.kind) {
    case 'permission':
      return 'Could not reset: the request was refused. Reload the page and try again.';
    case 'network':
      return 'Could not reset: we could not reach the server. Check your connection and try again.';
    default:
      return 'Could not reset. Please try again.';
  }
}

interface StudentViewResetDialogProps {
  componentId: string;
  componentTitle?: string;
  courseId: string;
  onOpenChange(nextOpen: boolean): void;
  open: boolean;
}

/**
 * Confirmation dialog for wiping the instructor's OWN test-student data for
 * this component (answers, attempts, and scores) so the component can be
 * experienced from scratch in student view. Real student data is never
 * affected.
 */
export function StudentViewResetDialog({
  componentId,
  componentTitle,
  courseId,
  onOpenChange,
  open,
}: StudentViewResetDialogProps) {
  const mutation = useResetStudentView();
  const wasOpen = useRef(false);

  useEffect(() => {
    if (open && !wasOpen.current) {
      mutation.reset();
    }
    wasOpen.current = open;
  }, [mutation, open]);

  async function handleReset() {
    try {
      await mutation.mutateAsync({ componentId, courseId });
      onOpenChange(false);
    } catch {
      // Error surfaced below via mutation.isError.
    }
  }

  return (
    <Dialog.Root onOpenChange={onOpenChange} open={open}>
      <Dialog.Portal>
        <Dialog.Overlay className={styles.overlay} />
        <Dialog.Content className={styles.content}>
          <div className={styles.header}>
            <Dialog.Title className={styles.title}>
              Reset student view data?
            </Dialog.Title>
            <Dialog.Description asChild>
              <p className={styles.description}>
                This deletes your test student&rsquo;s answers, attempts, and
                scores for{' '}
                <strong>{componentTitle ?? 'this component'}</strong>, so you
                can experience it again from scratch in student view.
              </p>
            </Dialog.Description>
          </div>

          <p className={styles.note} role="note">
            Only your own test account is reset — real students&rsquo; answers
            and grades are never affected.
          </p>

          {mutation.isError ? (
            <p aria-live="polite" className={styles.error} role="alert">
              {describeResetFailure(mutation.error)}
            </p>
          ) : null}

          <div className={styles.footer}>
            <Dialog.Close asChild>
              <Button variant="secondary">Cancel</Button>
            </Dialog.Close>
            <Button
              disabled={mutation.isPending}
              onClick={() => {
                void handleReset();
              }}
              variant="primary"
            >
              {mutation.isPending ? 'Resetting…' : 'Reset test data'}
            </Button>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
